import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { GridSortDirection } from "@mui/x-data-grid";
import { useQuery } from "@apollo/client";
import { GET_END_USERS } from "../../../graphql/queries";
import EndUsersTable from "../../../Components/Tables/EndUsersTable/EndUsersTable";
import PageContainer from "../../Shared/PageContainer/PageContainer";
import { EndUser } from "../../../../Types";
import toast from "react-hot-toast";
import FilterBuilder from "../../../Components/Shared/FilterBuilder/FilterBuilder";
import { filterListByMongoQuery } from "../../../Components/Shared/FilterBuilder/FilterBuilder.clientFilter";

const EndUsers = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<
    { field: string; sort: GridSortDirection }[]
  >([{ field: "createdAt", sort: "desc" }]);
  const [endUsers, setEndUsers] = useState<EndUser[]>([]);
  const [filterBuilderOpen, setFilterBuilderOpen] = useState(false);
  const [advancedFilter, setAdvancedFilter] = useState<string | null>(null);
  const [quickFilterText, setQuickFilterText] = useState("");

  // Get admin info for permissions
  const admin = useMemo(() => {
    const adminString = localStorage.getItem("admin");
    if (!adminString) return null;
    try {
      return JSON.parse(adminString);
    } catch (parseError) {
      console.error("Failed to parse admin from localStorage:", parseError);
      return null;
    }
  }, []);

  // Fetch end users (role: user)
  const { loading, error, data } = useQuery(GET_END_USERS, {
    variables: {
      limit: pageSize,
      page: page + 1, // GraphQL expects 1-based pagination
      type: "user",
    },
    fetchPolicy: "network-only",
    skip: !admin || (admin.role !== "superadmin" && admin.role !== "admin"),
  });

  useEffect(() => {
    if (data && data.users) {
      setEndUsers(data.users);
      // Backend does not return total count for this query.
      // To keep DataGrid next/prev arrows working in server pagination mode,
      // estimate whether there is a next page based on pageSize.
      const receivedCount = data.users.length;
      const baseCount = page * pageSize + receivedCount;
      const hasNextPage = receivedCount === pageSize;
      setRowCount(hasNextPage ? baseCount + 1 : baseCount);
    } else if (error) {
      toast.error("Error fetching end users");
      console.error("Error fetching end users:", error);
    }
  }, [data, error, page, pageSize]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  const handleSortModelChange = useCallback((newModel: any) => {
    setSortModel(newModel);
    // Note: You may need to implement server-side sorting if the API supports it
  }, []);

  const handleFilterModelChange = useCallback((model: any) => {
    const nextFilterText = Array.isArray(model?.quickFilterValues)
      ? model.quickFilterValues.join(" ").trim()
      : "";
    setQuickFilterText(nextFilterText.toLowerCase());
    setPage(0);
  }, []);

  const displayData = useMemo(() => {
    const advancedFiltered = advancedFilter
      ? filterListByMongoQuery(endUsers, advancedFilter)
      : endUsers;

    if (!quickFilterText) {
      return advancedFiltered;
    }

    return advancedFiltered.filter((user) => {
      const searchTarget = `${user.name || ""} ${user.email || ""} ${user.contact || ""}`.toLowerCase();
      return searchTarget.includes(quickFilterText);
    });
  }, [endUsers, advancedFilter, quickFilterText]);

  // Check if user has permission
  if (!admin || (admin.role !== "superadmin" && admin.role !== "admin")) {
    return (
      <PageContainer pt={"50px"} title="End Users">
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="error">
            You don't have permission to view this page.
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer pt={"50px"} title="End Users">
      <Box
        sx={{
          p: 3,
          height: "calc(100vh - 150px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          End Users Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          View and manage all end users registered in the system
        </Typography>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <EndUsersTable
          loading={loading}
          filteredData={displayData}
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterModelChange}
          onOpenAdvancedFilter={() => setFilterBuilderOpen(true)}
          hasActiveFilter={!!advancedFilter}
        />
        </Box>
      </Box>
      <FilterBuilder
        entityType="END_USER"
        open={filterBuilderOpen}
        onClose={() => setFilterBuilderOpen(false)}
        onApply={(filter) => {
          setAdvancedFilter(filter);
          setPage(0);
        }}
        pageId="end-users-table"
      />
    </PageContainer>
  );
};

export default EndUsers; 